import React from 'react';
import PropTypes from 'prop-types';
// @material-ui/core
import {
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  List,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  CalendarViewMonth,
  Analytics,
  NotificationsActive,
  Task,
  Search,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const drawerWidth = 225;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({
    theme,
    open,
  }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const pages = [
  {
    title: 'Planning',
    path: '/tasks/planning',
    staff: true,
    admin: true,
    owner: true,
    icon: <CalendarViewMonth />,
  },
  {
    title: 'Calendar',
    path: '/tasks/calendar',
    staff: true,
    admin: true,
    owner: true,
    icon: <CalendarMonth />,
  },
  {
    title: 'Search Tasks',
    path: '/tasks',
    staff: true,
    admin: true,
    owner: true,
    icon: <Search />,
  },
  {
    title: 'Reminders',
    path: '/tasks/reminders',
    staff: true,
    admin: true,
    owner: true,
    icon: <NotificationsActive />,
  },
  {
    title: 'Analytics',
    path: '/tasks/analytics',
    staff: true,
    admin: true,
    owner: true,
    icon: <Analytics />,
  },
];

const NavigationBarnComponent = ({
  navigate,
  path,
  open,
  setOpen,
}) => {
  const permissions = useSelector((state) => state.user.permissions)

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader sx={{ pt: '3rem' }}>
        {open && (
          <Box
            sx={{
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pl: 2,
            }}
          >
            <Task color="secondary" />
            <Typography
              variant="h5"
              color="secondary"
              sx={{
                ml: 1,
                fontWeight: 600,
              }}
            >Tasks
            </Typography>
          </Box>
        )}
        <Tooltip
          title={open
            ? 'Hide Drawer'
            : 'Show Drawer'}
        >
          <IconButton onClick={() => { if (open) { handleDrawerClose(); } else { handleDrawerOpen(); } }}>
            {!open
              ? <ChevronRight />
              : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </DrawerHeader>
      <Divider />
      <List sx={{ p: 0 }}>
        {
          pages.filter((p) => (p.owner && permissions.owner)
            || (p.admin && permissions.admin)
            || (p.staff && permissions.staff))
            .map((page) => (
              <ListItem key={page.title} disablePadding sx={{ display: 'block' }} divider>
                <Tooltip title={page.title}>
                  <ListItemButton
                    onClick={() => { navigate(page.path); }}
                    selected={page.path === path && !page.children}
                    sx={{
                      minHeight: 48,
                      justifyContent: open
                        ? 'initial'
                        : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open
                          ? 3
                          : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {page.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={page.title}
                      primaryTypographyProps={{
                        fontWeight: page.path === path
                          ? 600
                          : 400,
                      }}
                      sx={{
                        opacity: open
                          ? 1
                          : 0,
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>

            ))
        }
      </List>
    </Drawer>
  );
}

NavigationBarnComponent.propTypes = {
  navigate: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default NavigationBarnComponent;
